import { includes } from "@banjoanton/utils";

export const EngineModel = {
    Stockfish16: "Stockfish16",
    Stockfish17: "Stockfish17",
    Stockfish17Lite: "Stockfish17Lite",
} as const;

export type EngineModel = (typeof EngineModel)[keyof typeof EngineModel];

export const isEngineModel = (s: string): s is EngineModel =>
    includes(Object.values(EngineModel), s);

export type EngineConfig = {
    name: string;
    size: number;
    jsFile: string;
    wasmFiles: string[]; // relative to public
};

export const EngineConfig = {
    Stockfish16: {
        name: "Stockfish 16",
        size: 0.6 * 1024 * 1024, // 0.6 MB
        jsFile: "/engine/stockfish-16/stockfish.wasm.js",
        wasmFiles: ["/engine/stockfish-16/stockfish.wasm"],
    },
    Stockfish17: {
        name: "Stockfish 17",
        size: 78 * 1024 * 1024, // 78 MB
        jsFile: "/engine/stockfish-17/stockfish-17.1-single-a496a04.js",
        wasmFiles: [
            "/engine/stockfish-17/stockfish-17.1-single-a496a04-part-0.wasm",
            "/engine/stockfish-17/stockfish-17.1-single-a496a04-part-1.wasm",
            "/engine/stockfish-17/stockfish-17.1-single-a496a04-part-2.wasm",
            "/engine/stockfish-17/stockfish-17.1-single-a496a04-part-3.wasm",
            "/engine/stockfish-17/stockfish-17.1-single-a496a04-part-4.wasm",
            "/engine/stockfish-17/stockfish-17.1-single-a496a04-part-5.wasm",
        ],
    },
    // Stockfish17Multi: {
    //     name: "Stockfish 17 (Multi-threaded)",
    //     size: 78 * 1024 * 1024, // 78 MB (same as single-threaded)
    //     jsFile: "/engine/stockfish-17-multi/stockfish-17.1-8e4d048.js",
    //     wasmFiles: [
    //         "/engine/stockfish-17-multi/stockfish-17.1-8e4d048-part-0.wasm",
    //         "/engine/stockfish-17-multi/stockfish-17.1-8e4d048-part-1.wasm",
    //         "/engine/stockfish-17-multi/stockfish-17.1-8e4d048-part-2.wasm",
    //         "/engine/stockfish-17-multi/stockfish-17.1-8e4d048-part-3.wasm",
    //         "/engine/stockfish-17-multi/stockfish-17.1-8e4d048-part-4.wasm",
    //         "/engine/stockfish-17-multi/stockfish-17.1-8e4d048-part-5.wasm",
    //     ],
    // },
    Stockfish17Lite: {
        name: "Stockfish 17 Lite",
        size: 7 * 1024 * 1024, // 7 MB
        jsFile: "/engine/stockfish-17-lite/stockfish-17.1-lite-single-03e3232.js",
        wasmFiles: [
            "/engine/stockfish-17-lite/stockfish-17.1-lite-single-03e3232.wasm",
        ],
    },
} satisfies Record<EngineModel, EngineConfig>;

/**
 * Ordered list of chess engine models by strength.
 *
 * The order is from weakest to strongest:
 * - Stockfish16: Stockfish 16, older version, lowest strength
 * - Stockfish17Lite: Stockfish 17 Lite, improved over 16, but lighter than full 17
 * - Stockfish17: Stockfish 17, strongest engine in this list
 */
export const EngineModelOrder: EngineModel[] = [
    "Stockfish16",
    "Stockfish17Lite",
    "Stockfish17",
];

/**
 * Compares two engine models and determines if the first is better than the second. Same model is considered not better.
 *
 * @param toCompare - The engine model to compare.
 * @param baseModel - The base engine model to compare against.
 * @returns `true` if `toCompare` is better than `baseModel`, otherwise `false`.
 */
export const compare = (
    toCompare: EngineModel,
    baseModel: EngineModel
): boolean =>
    EngineModelOrder.indexOf(toCompare) > EngineModelOrder.indexOf(baseModel);

export const Engine = {
    list: Object.entries(EngineConfig).map(([key, config]) => ({
        key,
        ...config,
    })),
    get: (key: EngineModel) => EngineConfig[key],
    parse: (s: string): EngineModel => {
        if (s in EngineConfig) {
            return s as EngineModel;
        }
        return "Stockfish17";
    },
    compare,
};
